<template>
  <div class="preferred-shipping-list-container preferred-shipping-list-main preferred-shipping-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Preferred Shipping Methods']" />

    <div class="glass-tab-view glass-tab-view--flush preferred-shipping-list-glass-shell">
      <div class="glass-tab-view__content preferred-shipping-list-tab-content preferred-shipping-list-page-stack">
        <div class="preferred-shipping-list-header-block">
          <div class="preferred-shipping-list-header-row">
            <div class="preferred-shipping-list-header-row__text">
              <h2>Preferred Shipping Methods</h2>
              <p class="preferred-shipping-list-header-row__sub">
                Create and manage shipping methods for vendors and purchase orders
              </p>
            </div>
            <div class="preferred-shipping-list-header-actions">
              <PrimaryButton label="Add New" @click="showModal = true" />
            </div>
          </div>
        </div>

        <div class="preferred-shipping-list-glass-panel preferred-shipping-list-main-card">
          <div class="consultant-glass-table-section preferred-shipping-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': isLoading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table preferred-shipping-list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th class="preferred-shipping-list-delete-header">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="3" class="consultant-glass-table-no-records preferred-shipping-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="3" class="consultant-glass-table-no-records preferred-shipping-list-no-records">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="methods.length === 0">
                      <td colspan="3" class="consultant-glass-table-no-records preferred-shipping-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(method, idx) in methods"
                        :key="method.id || idx"
                        class="consultant-glass-table-row preferred-shipping-list-row"
                        :style="{ '--psl-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>{{ method.name }}</td>
                        <td>{{ method.description }}</td>
                        <td class="preferred-shipping-list-delete-cell">
                          <SecondaryButton
                            label="Delete"
                            icon="bi bi-trash"
                            custom-class="consultant-secondary-btn--danger preferred-shipping-list-delete-btn"
                            :disabled="isDeleting"
                            @click="deleteMethod(idx)"
                          />
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AddPreferredShippingMethod
      :visible="showModal"
      :companyId="companyId"
      @ok="handleShippingMethodCreated"
      @cancel="closeModal"
    />

    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      buttonText="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      buttonText="OK"
      @close="closeErrorModal"
    />

    <WarningModal
      :show="showWarningModal"
      title="Delete Shipping Method"
      :message="warningMessage"
      buttonText="Cancel"
      confirmButtonText="Delete"
      @close="closeWarningModal"
      @confirm="confirmDeleteMethod"
    />
  </div>
</template>

<script>
import '../styles/PreferredShippingMethodList.css';
import AddPreferredShippingMethod from '@/modules/vendor/views/AddPreferredShippingMethod.vue';
import logger from '@/utils/logger';
import ApiService from '@/services/api.service.js';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import WarningModal from '@/components/WarningModal.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';

export default {
  name: 'PreferredShippingMethodList',
  components: {
    AddPreferredShippingMethod,
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    WarningModal,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      showModal: false,
      methods: [],
      loading: false,
      error: '',
      companyId: this.$route.params.id,
      showSuccessModal: false,
      showErrorModal: false,
      showWarningModal: false,
      errorMessage: '',
      warningMessage: '',
      successMessage: '',
      methodToDelete: null,
      isDeleting: false,
    };
  },
  computed: {
    hasMethods() {
      return this.methods && this.methods.length > 0;
    },
    isLoading() {
      return this.loading || this.isDeleting;
    },
    canDelete() {
      return this.hasMethods && !this.isLoading;
    },
  },
  async mounted() {
    await this.fetchMethods();
  },
  methods: {
    async fetchMethods() {
      this.loading = true;
      this.error = '';
      try {
        const res = await ApiService.get(`/vendor/preferred-shipping-methods?companyId=${this.companyId}`);
        this.methods = Array.isArray(res.data) ? res.data : [];
        logger.log('Successfully fetched shipping methods:', this.methods.length);
      } catch (err) {
        logger.error('Error fetching shipping methods:', err);

        if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Shipping methods service not found. Please contact support.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to view shipping methods.';
        } else if (err.response?.status === 500) {
          this.errorMessage =
            'Internal server error occurred while fetching shipping methods. The server encountered an unexpected condition. Please try again in a few minutes.';
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage =
            'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while fetching shipping methods. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = 'An unexpected error occurred while fetching shipping methods. Please try again.';
        }
        this.showErrorModal = true;
      } finally {
        this.loading = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },

    async handleShippingMethodCreated(createdMethod) {
      this.closeModal();
      await this.fetchMethods();
      const methodCount = this.methods.length;
      this.successMessage = `Shipping method "${createdMethod?.name || 'New Shipping Method'}" created successfully! You now have ${methodCount} shipping method${methodCount !== 1 ? 's' : ''} in your system.`;
      this.showSuccessModal = true;
      logger.log('Shipping method created successfully, total methods:', methodCount);
    },

    deleteMethod(idx) {
      const method = this.methods[idx];
      if (!method || !method.id) {
        this.errorMessage = 'Invalid shipping method selected. Please refresh the page and try again.';
        this.showErrorModal = true;
        return;
      }

      if (!method.name || method.name.trim() === '') {
        this.errorMessage = 'Cannot delete a shipping method with an empty name.';
        this.showErrorModal = true;
        return;
      }

      this.methodToDelete = { method, idx };
      this.warningMessage = `Are you sure you want to delete the shipping method "${method.name}"? This action cannot be undone and may affect vendors assigned to this shipping method.`;
      this.showWarningModal = true;
      logger.log('Delete confirmation requested for shipping method:', method.name);
    },

    closeWarningModal() {
      this.showWarningModal = false;
      this.methodToDelete = null;
      this.warningMessage = '';
    },

    async confirmDeleteMethod() {
      if (!this.methodToDelete) return;

      const { method, idx } = this.methodToDelete;
      this.isDeleting = true;
      this.showWarningModal = false;

      try {
        logger.log('Attempting to delete shipping method:', method.name, 'ID:', method.id);
        await ApiService.deletePreferredShippingMethod(method.id);
        this.methods.splice(idx, 1);
        this.error = '';

        const remainingCount = this.methods.length;
        this.successMessage = `Shipping method "${method.name}" has been deleted successfully. You now have ${remainingCount} shipping method${remainingCount !== 1 ? 's' : ''} remaining.`;
        this.showSuccessModal = true;
        logger.log('Shipping method deleted successfully, remaining methods:', remainingCount);
      } catch (err) {
        logger.error('Error deleting shipping method:', err);

        const errorText =
          err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || err.message || '';

        if (
          errorText.includes('foreign key constraint') ||
          errorText.includes('violates foreign key') ||
          errorText.includes('referenced by') ||
          errorText.includes('cannot delete') ||
          errorText.includes('constraint') ||
          errorText.includes('vendors') ||
          errorText.includes('shippingMethodId') ||
          errorText.includes('ShippingMethods')
        ) {
          if (errorText.includes('vendors') || errorText.includes('vendor')) {
            this.errorMessage = `Cannot delete "${method.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another shipping method first.`;
          } else {
            this.errorMessage = `Cannot delete "${method.name}" because it is currently being used by other records. Please reassign those records to another shipping method first.`;
          }
        } else if (err.response?.data?.message) {
          this.errorMessage = err.response.data.message;
        } else if (err.response?.data?.error) {
          this.errorMessage = err.response.data.error;
        } else if (err.response?.data?.detail) {
          this.errorMessage = err.response.data.detail;
        } else if (err.response?.status === 409) {
          this.errorMessage = `Cannot delete "${method.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another shipping method first.`;
        } else if (err.response?.status === 404) {
          this.errorMessage = 'Shipping method not found. It may have already been deleted.';
        } else if (err.response?.status === 403) {
          this.errorMessage = 'You do not have permission to delete shipping methods.';
        } else if (err.response?.status === 500) {
          const detail = err.response?.data?.message || err.response?.data?.error || err.response?.data?.detail || '';
          if (
            detail.includes('foreign key constraint') ||
            detail.includes('violates foreign key') ||
            detail.includes('referenced by') ||
            detail.includes('vendors') ||
            detail.includes('shippingMethodId') ||
            detail.includes('ShippingMethods')
          ) {
            this.errorMessage = `Cannot delete "${method.name}" because it is currently assigned to one or more vendors. Please reassign those vendors to another shipping method first.`;
          } else {
            this.errorMessage =
              'Internal server error occurred while deleting the shipping method. The server encountered an unexpected condition. Please try again in a few minutes.';
          }
        } else if (err.response?.status === 502) {
          this.errorMessage =
            'Bad gateway error. The server received an invalid response from another server. Please try again later.';
        } else if (err.response?.status === 503) {
          this.errorMessage =
            'Service temporarily unavailable. The server is currently unable to handle the request. Please try again later.';
        } else if (err.response?.status === 504) {
          this.errorMessage = 'Gateway timeout. The server did not receive a timely response. Please try again later.';
        } else if (err.response?.status >= 500) {
          this.errorMessage = `Server error (${err.response.status}) occurred while deleting the shipping method. Please try again later or contact support if the problem persists.`;
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          this.errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (err.message) {
          this.errorMessage = err.message;
        } else {
          this.errorMessage = `An unexpected error occurred while deleting "${method.name}". Please try again.`;
        }
        this.showErrorModal = true;
      } finally {
        this.isDeleting = false;
        this.methodToDelete = null;
        this.warningMessage = '';
      }
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
    },
  },
};
</script>
